// Creative Strategist Tasks tab (Task #2378, spec §52/§53).
//
// Two views over the same task graph — a list and a board — plus a detail
// view. A card carries its dependency state, not just its status, because
// "Not started" on its own never explains why a task is idle.
//
// Sections owned by later phases (output, review, version history) render as
// explicit empty states rather than being hidden, so the shape of the detail
// view is honest about what is coming.

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  Ban,
  CheckCircle2,
  CircleSlash,
  Clock,
  Columns3,
  FileText,
  Layers,
  ListChecks,
  Loader2,
  Lock,
  Package,
  Play,
  RefreshCw,
  Rows3,
  UserCheck,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  CS_REVIEW_VERDICT_CLASSES,
  CS_REVIEW_VERDICT_LABELS,
} from "@shared/creativeStrategistDeliverables";
import {
  CS_BACKFILL_TASK_TYPES,
  CS_DECISION_SOURCE_CLASSES,
  CS_DECISION_SOURCE_LABELS,
  CS_DECISION_STATUS_CLASSES,
  CS_DECISION_STATUS_LABELS,
  CS_DECISION_TYPE_LABELS,
  CS_EXECUTOR_LABELS,
  CS_PLAN_GROUP_LABELS,
  CS_TASK_BOARD_COLUMNS,
  CS_TASK_STATUS_CLASSES,
  CS_TASK_STATUS_LABELS,
  csCanCancelTask,
  csDecisionsKey,
  csFormatDateTime,
  csIsBackfillTask,
  csIsToolTask,
  csRunTaskBlockedReason,
  csTaskKey,
  csTasksKey,
  csTasksPollInterval,
  csTaskStillMoving,
  csToolRunBlockedReason,
  CS_TASKS_POLL_MS,
  type CreativeStrategistExecution,
  type CreativeStrategistPlanGroup,
  type CreativeStrategistTask,
  type CreativeStrategistTaskDetail,
} from "@/lib/creative-strategist";

interface TasksTabProps {
  projectId: string;
  /** Project status — polling keeps watching an empty/idle list while the
   * project can still produce tasks (Task #2422). */
  projectStatus?: string | null;
  selectedTaskId: string | null;
  onSelectTask: (taskId: string | null) => void;
}

export function CreativeStrategistTasksTab({
  projectId,
  projectStatus,
  selectedTaskId,
  onSelectTask,
}: TasksTabProps) {
  const [view, setView] = useState<"list" | "board">("list");

  // Conditional polling (repo convention): the board refreshes itself while
  // something can still move — including the "tab opened before the plan was
  // approved" case, where the list is empty but the project is active and the
  // approval is about to create tasks (Task #2422). An idle plan still costs
  // nothing.
  const { data: tasks, isLoading } = useQuery<CreativeStrategistTask[]>({
    queryKey: [csTasksKey(projectId)],
    enabled: !!projectId,
    refetchInterval: (query) =>
      csTasksPollInterval(
        query.state.data as CreativeStrategistTask[] | undefined,
        projectStatus,
      ),
  });

  if (selectedTaskId) {
    return (
      <TaskDetail taskId={selectedTaskId} onBack={() => onSelectTask(null)} />
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="tasks-loading">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (!tasks || tasks.length === 0) {
    return (
      <Card>
        <CardContent className="py-14 text-center space-y-2" data-testid="empty-tasks">
          <ListChecks className="h-6 w-6 mx-auto text-muted-foreground" />
          <div className="text-sm font-medium">No tasks yet</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Tasks appear once you approve a plan. Each one tracks its own status, inputs
            and output.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="tasks-tab">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground" data-testid="text-task-count">
          {tasks.length} {tasks.length === 1 ? "task" : "tasks"}
        </div>
        <div className="flex items-center gap-1 rounded-md border p-0.5">
          <Button
            size="sm"
            variant={view === "list" ? "secondary" : "ghost"}
            className="h-7 px-2"
            onClick={() => setView("list")}
            data-testid="button-tasks-list-view"
          >
            <Rows3 className="h-3.5 w-3.5 mr-1.5" /> List
          </Button>
          <Button
            size="sm"
            variant={view === "board" ? "secondary" : "ghost"}
            className="h-7 px-2"
            onClick={() => setView("board")}
            data-testid="button-tasks-board-view"
          >
            <Columns3 className="h-3.5 w-3.5 mr-1.5" /> Board
          </Button>
        </div>
      </div>

      {view === "list" ? (
        <TaskListView tasks={tasks} onSelect={onSelectTask} />
      ) : (
        <TaskBoardView tasks={tasks} onSelect={onSelectTask} />
      )}
    </div>
  );
}

function TaskListView({
  tasks,
  onSelect,
}: {
  tasks: CreativeStrategistTask[];
  onSelect: (id: string) => void;
}) {
  // Group by the plan section the task came from — the same order the Plan tab
  // used, so the two tabs read as one story.
  const groups = useMemo(() => {
    const byGroup = new Map<string, CreativeStrategistTask[]>();
    for (const task of tasks) {
      const key = task.planGroup ?? "other";
      byGroup.set(key, [...(byGroup.get(key) ?? []), task]);
    }
    return [...byGroup.entries()];
  }, [tasks]);

  return (
    <div className="space-y-4" data-testid="tasks-list-view">
      {groups.map(([group, groupTasks]) => (
        <Card key={group}>
          <CardContent className="p-4 space-y-2">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {CS_PLAN_GROUP_LABELS[group as CreativeStrategistPlanGroup] ?? "Other"}
            </div>
            {groupTasks.map((task) => (
              <TaskCard key={task.id} task={task} onSelect={onSelect} />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TaskBoardView({
  tasks,
  onSelect,
}: {
  tasks: CreativeStrategistTask[];
  onSelect: (id: string) => void;
}) {
  // Statuses outside the five canonical columns (blocked, failed, canceled,
  // internal_review) fold into the nearest column so no task can vanish.
  const columnFor = (status: string): string => {
    if ((CS_TASK_BOARD_COLUMNS as readonly string[]).includes(status)) return status;
    if (status === "blocked" || status === "failed") return "not_started";
    // Review and revision are the system still working on it, not the user's
    // queue — they belong under Running until a verdict routes them out.
    if (status === "internal_review" || status === "revision") return "running";
    if (status === "canceled") return "complete";
    return "not_started";
  };

  return (
    <div
      className="grid gap-3 md:grid-cols-3 lg:grid-cols-5 items-start"
      data-testid="tasks-board-view"
    >
      {CS_TASK_BOARD_COLUMNS.map((column) => {
        const columnTasks = tasks.filter((t) => columnFor(t.status) === column);
        return (
          <div key={column} className="space-y-2" data-testid={`board-column-${column}`}>
            <div className="flex items-center justify-between text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
              <span>{CS_TASK_STATUS_LABELS[column] ?? column}</span>
              <span>{columnTasks.length}</span>
            </div>
            <div className="space-y-2">
              {columnTasks.map((task) => (
                <TaskCard key={task.id} task={task} onSelect={onSelect} compact />
              ))}
              {columnTasks.length === 0 && (
                <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground text-center">
                  Empty
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Run (or retry) a setup backfill — the task that turns something the user
 * described into a real persona, offer or saved angle. Server-side the run is
 * idempotent, so a second press returns the object the first one made rather
 * than creating a duplicate.
 */
function RunTaskButton({
  task,
  showWhenBlocked,
}: {
  task: CreativeStrategistTask;
  /** Detail view keeps the button visible (disabled) and explains why. */
  showWhenBlocked?: boolean;
}) {
  const { toast } = useToast();
  const blockedReason = csRunTaskBlockedReason(task);
  const objectLabel = CS_BACKFILL_TASK_TYPES[task.taskType ?? ""] ?? "object";

  const run = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/creative-strategist/tasks/${task.id}/run`,
      );
      return (await res.json()) as { reused?: boolean; objectType?: string | null };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [csTasksKey(task.projectId)] });
      queryClient.invalidateQueries({ queryKey: [csTaskKey(task.id)] });
      queryClient.invalidateQueries({ queryKey: [csDecisionsKey(task.projectId)] });
      queryClient.invalidateQueries({
        queryKey: [`/api/creative-strategist/projects/${task.projectId}`],
      });
      toast({
        title: data?.reused ? "Already created" : `Created the ${objectLabel}`,
        description: data?.reused
          ? "This task had already produced its object, so nothing was duplicated."
          : "It is a normal PersonaOS object now — open it from the task's Created objects.",
      });
    },
    onError: (e: any) => {
      queryClient.invalidateQueries({ queryKey: [csTasksKey(task.projectId)] });
      queryClient.invalidateQueries({ queryKey: [csTaskKey(task.id)] });
      toast({
        title: "Could not run that task",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  if (!csIsBackfillTask(task)) return null;
  if (blockedReason && !showWhenBlocked) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        size="sm"
        variant={task.status === "failed" ? "outline" : "default"}
        className="h-7 px-2"
        disabled={!!blockedReason || run.isPending}
        onClick={() => run.mutate()}
        data-testid={`button-run-task-${task.id}`}
      >
        {run.isPending ? (
          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        ) : (
          <Play className="h-3.5 w-3.5 mr-1.5" />
        )}
        {run.isPending
          ? "Creating…"
          : task.status === "failed"
            ? `Try the ${objectLabel} again`
            : `Create the ${objectLabel}`}
      </Button>
      {blockedReason ? (
        <span
          className="text-[11px] text-muted-foreground"
          data-testid={`text-run-blocked-${task.id}`}
        >
          {blockedReason}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Run / retry a tool task (Task #2381). The executor picks ready work up on
 * its own, so this is "do it now" and, after a failure, "try again" — the
 * disabled reasons mirror the executor's own admission rule.
 */
function RunToolTaskButton({
  task,
  showWhenBlocked,
}: {
  task: CreativeStrategistTask;
  showWhenBlocked?: boolean;
}) {
  const { toast } = useToast();
  const blockedReason = csToolRunBlockedReason(task);

  const run = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/creative-strategist/tasks/${task.id}/run`,
      );
      return (await res.json()) as { message?: string };
    },
    onSuccess: (data) => {
      invalidateTask(task);
      toast({
        title: "Queued",
        description: data?.message ?? "Creative Strategist is starting this task.",
      });
    },
    onError: (e: any) => {
      invalidateTask(task);
      toast({
        title: "Could not start that task",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  if (!csIsToolTask(task)) return null;
  if (blockedReason && !showWhenBlocked) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        size="sm"
        variant={task.status === "failed" ? "outline" : "default"}
        className="h-7 px-2"
        disabled={!!blockedReason || run.isPending}
        onClick={() => run.mutate()}
        data-testid={`button-run-task-${task.id}`}
      >
        {run.isPending ? (
          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        ) : task.status === "failed" ? (
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
        ) : (
          <Play className="h-3.5 w-3.5 mr-1.5" />
        )}
        {task.status === "failed" ? "Try again" : "Run now"}
      </Button>
      {blockedReason ? (
        <span
          className="text-[11px] text-muted-foreground"
          data-testid={`text-run-blocked-${task.id}`}
        >
          {blockedReason}
        </span>
      ) : null}
    </div>
  );
}

function CancelTaskButton({ task }: { task: CreativeStrategistTask }) {
  const { toast } = useToast();
  const cancel = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/creative-strategist/tasks/${task.id}/cancel`,
      );
      return (await res.json()) as { message?: string };
    },
    onSuccess: (data) => {
      invalidateTask(task);
      toast({
        title: "Cancelled",
        description: data?.message ?? "This task was cancelled.",
      });
    },
    onError: (e: any) => {
      invalidateTask(task);
      toast({
        title: "Could not cancel",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  if (!csCanCancelTask(task)) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 px-2"
      disabled={cancel.isPending}
      onClick={() => cancel.mutate()}
      data-testid={`button-cancel-task-${task.id}`}
    >
      {cancel.isPending ? (
        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
      ) : (
        <CircleSlash className="h-3.5 w-3.5 mr-1.5" />
      )}
      Cancel
    </Button>
  );
}

function invalidateTask(task: Pick<CreativeStrategistTask, "id" | "projectId">) {
  queryClient.invalidateQueries({ queryKey: [csTasksKey(task.projectId)] });
  queryClient.invalidateQueries({ queryKey: [csTaskKey(task.id)] });
  queryClient.invalidateQueries({
    queryKey: [`/api/creative-strategist/projects/${task.projectId}`],
  });
}

/** Spec §52 card fields. */
function TaskCard({
  task,
  onSelect,
  compact,
}: {
  task: CreativeStrategistTask;
  onSelect: (id: string) => void;
  compact?: boolean;
}) {
  const runnable =
    (csIsBackfillTask(task) && !csRunTaskBlockedReason(task)) ||
    (csIsToolTask(task) && !csToolRunBlockedReason(task)) ||
    csCanCancelTask(task);
  return (
    <div className="rounded-lg border hover:bg-accent/50 transition-colors">
    <button
      type="button"
      onClick={() => onSelect(task.id)}
      className="w-full text-left p-3"
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium min-w-0">{task.title}</span>
        <Badge
          variant="secondary"
          className={`shrink-0 ${CS_TASK_STATUS_CLASSES[task.status] ?? ""}`}
          data-testid={`task-status-${task.id}`}
        >
          {CS_TASK_STATUS_LABELS[task.status] ?? task.status}
        </Badge>
      </div>
      <div
        className={`mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground ${compact ? "" : ""}`}
      >
        <span data-testid={`task-executor-${task.id}`}>
          {CS_EXECUTOR_LABELS[task.assignedExecutor ?? ""] ??
            task.assignedExecutor ??
            "Unassigned"}
        </span>
        <span data-testid={`task-dependency-state-${task.id}`}>{task.dependencyState}</span>
        {task.expectedOutputType ? (
          <span data-testid={`task-output-type-${task.id}`}>{task.expectedOutputType}</span>
        ) : null}
        <span>
          {task.attemptCount > 0 ? `attempt ${task.attemptCount}` : "not attempted"}
        </span>
        {task.requiresUserAction ? (
          <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <UserCheck className="h-3 w-3" /> Needs you
          </span>
        ) : null}
        {/* Out of automatic revisions, or escalated by review. The reason is
            the point — "needs you" alone tells the user nothing. */}
        {task.attentionReason ? (
          <span
            className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"
            title={task.attentionReason}
            data-testid={`task-attention-${task.id}`}
          >
            <AlertTriangle className="h-3 w-3" /> {task.attentionReason}
          </span>
        ) : null}
        {task.revisionCount > 0 ? (
          <span className="inline-flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> revision {task.revisionCount}
          </span>
        ) : null}
        {task.unmetDependencyCount > 0 ? (
          <span className="inline-flex items-center gap-1">
            <Lock className="h-3 w-3" /> waiting
          </span>
        ) : null}
        {(task.blockedByDecisions ?? []).length > 0 ? (
          <span
            className="inline-flex items-center gap-1 text-red-600 dark:text-red-400"
            data-testid={`task-rejected-decision-${task.id}`}
          >
            <Ban className="h-3 w-3" /> rejected decision
          </span>
        ) : null}
      </div>
    </button>
      {runnable ? (
        <div className="px-3 pb-3 flex items-center gap-2 flex-wrap">
          <RunTaskButton task={task} />
          <RunToolTaskButton task={task} />
          <CancelTaskButton task={task} />
        </div>
      ) : null}
    </div>
  );
}

/** Spec §53 task detail. */
function TaskDetail({ taskId, onBack }: { taskId: string; onBack: () => void }) {
  const { data: task, isLoading } = useQuery<CreativeStrategistTaskDetail>({
    queryKey: [csTaskKey(taskId)],
    enabled: !!taskId,
    // Same wedge shape as the list (Task #2422): a watched `not_started` (or
    // blocked / in-review) task changes on its own, so keep polling until it
    // settles — complete, canceled and failed only move via a user action,
    // which invalidates the cache anyway.
    refetchInterval: (query) => {
      const t = query.state.data as CreativeStrategistTaskDetail | undefined;
      return t && csTaskStillMoving(t) ? CS_TASKS_POLL_MS : false;
    },
  });

  if (isLoading) {
    return <Skeleton className="h-64 w-full" data-testid="task-detail-loading" />;
  }
  if (!task) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-3">
          <div className="text-sm text-muted-foreground">This task could not be loaded.</div>
          <Button variant="outline" onClick={onBack} data-testid="button-back-to-tasks">
            Back to tasks
          </Button>
        </CardContent>
      </Card>
    );
  }

  const inputs = (task.inputsJson ?? {}) as Record<string, any>;
  const criteria = Array.isArray(task.acceptanceCriteriaJson)
    ? task.acceptanceCriteriaJson
    : [];
  const decisionRefs = Array.isArray(task.decisionRefsJson) ? task.decisionRefsJson : [];
  const dependencies = Array.isArray(inputs.dependencies) ? inputs.dependencies : [];
  const decisions = task.decisions ?? [];
  const rejected = task.blockedByDecisions ?? [];
  const executions = task.executions ?? [];

  return (
    <div className="space-y-4" data-testid={`task-detail-${task.id}`}>
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid="link-back-to-tasks"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All tasks
      </button>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold" data-testid="text-task-title">
                {task.title}
              </h2>
              <div className="text-xs text-muted-foreground mt-1">
                {CS_EXECUTOR_LABELS[task.assignedExecutor ?? ""] ??
                  task.assignedExecutor ??
                  "Unassigned"}
                {task.taskType ? ` · ${task.taskType}` : ""}
                {task.createdAt ? ` · created ${csFormatDateTime(task.createdAt)}` : ""}
              </div>
            </div>
            <Badge
              variant="secondary"
              className={CS_TASK_STATUS_CLASSES[task.status] ?? ""}
              data-testid="badge-task-status"
            >
              {CS_TASK_STATUS_LABELS[task.status] ?? task.status}
            </Badge>
          </div>

          {rejected.length > 0 && (
            <div
              className="rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-xs"
              data-testid="task-blocked-by-decision"
            >
              <span className="font-medium text-red-700 dark:text-red-300">
                On hold — you rejected{" "}
                {rejected
                  .map(
                    (d) =>
                      d.title ??
                      CS_DECISION_TYPE_LABELS[d.decisionType] ??
                      d.decisionType,
                  )
                  .join(", ")}
                .
              </span>{" "}
              <span className="text-red-700/80 dark:text-red-300/80">
                This task will not run until that decision is settled on the Decisions tab.
              </span>
            </div>
          )}

          {csIsBackfillTask(task) && (
            <div className="rounded-md border bg-muted/30 px-3 py-3 space-y-2" data-testid="task-run-panel">
              <div className="text-xs text-muted-foreground">
                {task.status === "complete"
                  ? "This setup task has run."
                  : `Creative Strategist can create the ${
                      CS_BACKFILL_TASK_TYPES[task.taskType ?? ""] ?? "object"
                    } from what you described. It becomes a normal PersonaOS object.`}
              </div>
              <RunTaskButton task={task} showWhenBlocked />
              {task.lastError ? (
                <div
                  className="text-xs text-red-600 dark:text-red-400"
                  data-testid="text-task-last-error"
                >
                  {task.lastError}
                </div>
              ) : null}
            </div>
          )}

          {csIsToolTask(task) && (
            <div
              className="rounded-md border bg-muted/30 px-3 py-3 space-y-2"
              data-testid="task-tool-run-panel"
            >
              <div className="text-xs text-muted-foreground">
                {task.status === "complete"
                  ? `${CS_EXECUTOR_LABELS[task.assignedExecutor ?? ""] ?? "The tool"} has produced this task's output.`
                  : task.status === "running"
                    ? `Running in ${CS_EXECUTOR_LABELS[task.assignedExecutor ?? ""] ?? "the tool"} — the output lands here when it finishes.`
                    : `Creative Strategist runs this through ${
                        CS_EXECUTOR_LABELS[task.assignedExecutor ?? ""] ?? "the tool"
                      }. It starts on its own once everything it depends on is done.`}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <RunToolTaskButton task={task} showWhenBlocked />
                <CancelTaskButton task={task} />
              </div>
              {task.lastError ? (
                <div
                  className="text-xs text-red-600 dark:text-red-400"
                  data-testid="text-task-last-error"
                >
                  {task.lastError}
                </div>
              ) : null}
            </div>
          )}

          <DetailSection title="Objective" testId="task-section-objective">
            <p className="text-sm">
              {task.description || inputs.objective || "No objective recorded."}
            </p>
            {criteria.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground list-disc pl-4">
                {criteria.map((c, i) => (
                  <li key={i}>{String(c)}</li>
                ))}
              </ul>
            )}
          </DetailSection>

          <DetailSection title="Inputs" testId="task-section-inputs">
            {dependencies.length === 0 ? (
              <EmptyLine text="No prerequisite inputs recorded." />
            ) : (
              <div className="space-y-1.5">
                {dependencies.map((dep: any, i: number) => (
                  <div key={i} className="text-xs flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">
                      {String(dep.status ?? "")}
                    </Badge>
                    <span className="font-medium">{String(dep.type ?? "")}</span>
                    {dep.name ? (
                      <span className="text-muted-foreground">{String(dep.name)}</span>
                    ) : null}
                    {dep.suppliedText ? (
                      <span className="text-muted-foreground italic">
                        “{String(dep.suppliedText).slice(0, 160)}”
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
            {(task.inputsSent ?? []).length > 0 && (
              <div className="mt-3 space-y-1" data-testid="task-inputs-sent">
                <div className="text-[11px] font-medium text-muted-foreground">
                  Sent to {CS_EXECUTOR_LABELS[task.assignedExecutor ?? ""] ?? "the tool"}
                </div>
                {(task.inputsSent ?? []).map((row, i) => (
                  <div key={i} className="text-xs flex flex-wrap gap-2">
                    <span className="text-muted-foreground">{row.label}</span>
                    <span className="font-medium">{row.value}</span>
                  </div>
                ))}
              </div>
            )}
            {task.expectedOutputType ? (
              <div className="text-xs text-muted-foreground mt-2">
                Expected output: {task.expectedOutputType}
              </div>
            ) : null}
          </DetailSection>

          <DetailSection title="Files" testId="task-section-files">
            {task.files.length === 0 ? (
              <EmptyLine text="No files attached to this task." />
            ) : (
              <div className="space-y-1">
                {task.files.map((f) => (
                  <div key={f.id} className="text-xs flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    {f.fileName}
                    <span className="text-muted-foreground">{f.purpose}</span>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>

          <DetailSection title="Decisions" testId="task-section-decisions">
            {decisions.length === 0 ? (
              decisionRefs.length === 0 ? (
                <EmptyLine text="No decisions referenced yet." />
              ) : (
                <div className="text-xs text-muted-foreground">
                  {decisionRefs.length}{" "}
                  {decisionRefs.length === 1 ? "decision" : "decisions"} referenced.
                </div>
              )
            ) : (
              <div className="space-y-1.5">
                {decisions.map((d) => (
                  <div
                    key={d.id}
                    className="text-xs flex flex-wrap items-center gap-2"
                    data-testid={`task-decision-${d.id}`}
                  >
                    <Badge
                      variant="secondary"
                      className={`text-[10px] ${CS_DECISION_SOURCE_CLASSES[d.sourceType] ?? ""}`}
                    >
                      {CS_DECISION_SOURCE_LABELS[d.sourceType] ?? d.sourceType}
                    </Badge>
                    <span className="font-medium">
                      {CS_DECISION_TYPE_LABELS[d.decisionType] ?? d.decisionType}
                    </span>
                    <span className="text-muted-foreground">{d.title ?? ""}</span>
                    <Badge
                      variant="secondary"
                      className={`text-[10px] ${CS_DECISION_STATUS_CLASSES[d.status] ?? ""}`}
                    >
                      {CS_DECISION_STATUS_LABELS[d.status] ?? d.status}
                    </Badge>
                    <span className="text-muted-foreground">v{d.version}</span>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>

          <DetailSection title="Created objects" testId="task-section-created-objects">
            {task.createdObjects.length === 0 ? (
              <EmptyLine text="This task has not created anything in PersonaOS yet." />
            ) : (
              <div className="space-y-1.5">
                {task.createdObjects.map((obj) => (
                  <div
                    key={obj.id}
                    className="text-xs flex flex-wrap items-center gap-2"
                    data-testid={`task-created-object-${obj.id}`}
                  >
                    <Package className="h-3.5 w-3.5 text-muted-foreground" />
                    {obj.href ? (
                      <Link
                        href={obj.href}
                        className="font-medium hover:underline inline-flex items-center gap-1"
                        data-testid={`link-created-object-${obj.id}`}
                      >
                        {obj.objectType}
                        <ArrowUpRight className="h-3 w-3" />
                      </Link>
                    ) : (
                      <span className="font-medium">{obj.objectType}</span>
                    )}
                    <Badge
                      variant="secondary"
                      className={`text-[10px] ${CS_DECISION_SOURCE_CLASSES[obj.sourceType] ?? ""}`}
                    >
                      {CS_DECISION_SOURCE_LABELS[obj.sourceType] ?? obj.sourceType}
                    </Badge>
                    <span className="text-muted-foreground">
                      {csFormatDateTime(obj.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>

          <DetailSection title="Dependencies" testId="task-section-dependencies">
            {task.dependsOn.length === 0 && task.blocking.length === 0 ? (
              <EmptyLine text="No prerequisites — this task is ready as soon as the plan starts." />
            ) : (
              <div className="space-y-1.5 text-xs">
                {task.dependsOn.map((d) => (
                  <div key={d.id} className="flex items-center gap-2">
                    <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                    Waits on <span className="font-medium">{d.title}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {CS_TASK_STATUS_LABELS[d.status] ?? d.status}
                    </Badge>
                  </div>
                ))}
                {task.blocking.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-muted-foreground">
                    <Layers className="h-3.5 w-3.5" />
                    Blocks <span className="font-medium">{d.title}</span>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>

          <DetailSection title="Assigned tool" testId="task-section-tool">
            <div className="text-sm">
              {CS_EXECUTOR_LABELS[task.assignedExecutor ?? ""] ??
                task.assignedExecutor ??
                "Unassigned"}
            </div>
            {executions.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">
                {csIsToolTask(task)
                  ? "Not started yet."
                  : "This task does not run through a tool."}
              </p>
            ) : (
              <div className="mt-2 space-y-1.5" data-testid="task-executions">
                {executions.map((e) => (
                  <ExecutionLine key={e.id} execution={e} />
                ))}
              </div>
            )}
          </DetailSection>

          <DetailSection title="Output" testId="task-section-output">
            {!task.output ||
            (task.output.deliverables.length === 0 && task.output.links.length === 0) ? (
              <EmptyLine text="Nothing produced yet — outputs land here once the task runs." />
            ) : (
              <div className="space-y-3">
                {task.output.deliverables.map((d) => (
                  <div key={d.id} className="flex items-start gap-3" data-testid={`deliverable-${d.id}`}>
                    {d.previewUrl ? (
                      <img
                        src={d.previewUrl}
                        alt=""
                        className="h-16 w-16 rounded border object-cover shrink-0"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded border bg-muted flex items-center justify-center shrink-0">
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{d.title}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {d.deliverableType} · {d.status}
                        {d.createdAt ? ` · ${csFormatDateTime(d.createdAt)}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
                {task.output.links.length > 0 && (
                  <div className="space-y-1" data-testid="task-output-links">
                    {task.output.links.map((l) => (
                      <div key={l.id} className="text-xs flex items-center gap-2">
                        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-medium">{l.outputType}</span>
                        <span className="text-muted-foreground">in {l.outputSystem}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DetailSection>

          <DetailSection title="Review" testId="task-section-review">
            {!task.review ? (
              <EmptyLine text="No review yet." />
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant="secondary"
                    className={CS_REVIEW_VERDICT_CLASSES[task.review.verdict] ?? ""}
                    data-testid="task-review-verdict"
                  >
                    {CS_REVIEW_VERDICT_LABELS[task.review.verdict] ?? task.review.verdict}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    on version {task.review.versionNumber}
                  </span>
                  {typeof task.review.score === "number" && (
                    <span className="text-xs text-muted-foreground">
                      · score {task.review.score}/100
                    </span>
                  )}
                  {task.review.reviewedAt && (
                    <span className="text-xs text-muted-foreground">
                      · {csFormatDateTime(task.review.reviewedAt)}
                    </span>
                  )}
                </div>
                {task.review.summary && <p className="text-xs">{task.review.summary}</p>}
                {task.review.issues.length > 0 && (
                  <ul className="space-y-1">
                    {task.review.issues.map((issue, i) => (
                      <li
                        key={i}
                        className="text-xs text-muted-foreground flex items-start gap-1.5"
                      >
                        <span className="mt-1 h-1 w-1 rounded-full bg-current shrink-0" />
                        <span>
                          {issue.area ? <span className="font-medium">{issue.area}: </span> : null}
                          {issue.detail}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {task.review.criteriaChecks.length > 0 && (
                  <div className="grid gap-1 pt-0.5">
                    {task.review.criteriaChecks.map((c, i) => (
                      <div key={i} className="text-[11px] flex items-start gap-1.5">
                        {c.met ? (
                          <CheckCircle2 className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
                        ) : (
                          <AlertTriangle className="h-3 w-3 mt-0.5 text-amber-600 shrink-0" />
                        )}
                        <span className={c.met ? "text-muted-foreground" : ""}>
                          {c.criterion}
                          {c.note ? ` — ${c.note}` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {task.attentionReason && (
                  <div
                    className="rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 p-2.5 space-y-1"
                    data-testid="task-attention-reason"
                  >
                    <div className="text-xs font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5" /> Needs your attention
                    </div>
                    <p className="text-xs text-muted-foreground">{task.attentionReason}</p>
                    <p className="text-[11px] text-muted-foreground">
                      Open it in Deliverables to approve the work as it stands, or say what to
                      change.
                    </p>
                  </div>
                )}
                {task.revisionCount > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    {task.revisionCount} automatic{" "}
                    {task.revisionCount === 1 ? "revision" : "revisions"} used.
                  </p>
                )}
              </div>
            )}
          </DetailSection>

          <DetailSection title="Version history" testId="task-section-versions">
            {task.versionHistory.length === 0 ? (
              <EmptyLine text="No versions yet." />
            ) : (
              <div className="space-y-2">
                {[...task.versionHistory]
                  .sort((a, b) => b.versionNumber - a.versionNumber)
                  .map((v) => (
                    <div
                      key={v.id}
                      className="flex items-start gap-3 rounded-md border p-2.5"
                      data-testid={`task-version-${v.versionNumber}`}
                    >
                      {v.thumbnailUrl ? (
                        <img
                          src={v.thumbnailUrl}
                          alt={`Version ${v.versionNumber}`}
                          loading="lazy"
                          className="h-12 w-12 rounded object-cover bg-muted shrink-0"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded bg-muted flex items-center justify-center shrink-0">
                          <Package className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium">Version {v.versionNumber}</span>
                          <Badge
                            variant="secondary"
                            className={`text-[10px] h-4 px-1.5 ${CS_REVIEW_VERDICT_CLASSES[v.reviewVerdict] ?? ""}`}
                          >
                            {CS_REVIEW_VERDICT_LABELS[v.reviewVerdict] ?? v.reviewVerdict}
                          </Badge>
                          {v.createdAt && (
                            <span className="text-[11px] text-muted-foreground">
                              {csFormatDateTime(v.createdAt)}
                            </span>
                          )}
                        </div>
                        {v.reviewSummary && (
                          <p className="text-[11px] text-muted-foreground">{v.reviewSummary}</p>
                        )}
                      </div>
                    </div>
                  ))}
                {task.revisions.length > 0 && (
                  <div className="space-y-1 pt-1" data-testid="task-revision-chain">
                    {task.revisions.map((r) => (
                      <div key={r.id} className="text-[11px] text-muted-foreground">
                        <span className="font-medium text-foreground">
                          Revision {r.attempt ?? "?"}
                        </span>{" "}
                        · {CS_TASK_STATUS_LABELS[r.status] ?? r.status}
                        {r.instructions.length > 0 ? ` — ${r.instructions.join(" ")}` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DetailSection>

          <DetailSection title="Activity" testId="task-section-activity">
            {task.activity.length === 0 ? (
              <EmptyLine text="Nothing has happened on this task yet." />
            ) : (
              <div className="space-y-1.5">
                {task.activity.map((event) => (
                  <div key={event.id} className="text-xs flex items-start gap-2">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <span>
                      {event.summary}
                      <span className="text-muted-foreground">
                        {" "}
                        · {csFormatDateTime(event.createdAt)}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>
        </CardContent>
      </Card>
    </div>
  );
}

const EXECUTION_STATUS_LABELS: Record<string, string> = {
  pending: "Queued",
  dispatched: "Starting",
  running: "Running",
  complete: "Finished",
  failed: "Failed",
  canceled: "Cancelled",
};

function ExecutionLine({ execution }: { execution: CreativeStrategistExecution }) {
  const finished = execution.completedAt ?? null;
  return (
    <div className="text-xs flex flex-wrap items-center gap-2" data-testid={`execution-${execution.id}`}>
      <Badge variant="outline" className="text-[10px]">
        Attempt {execution.attemptNumber}
      </Badge>
      <span className="font-medium">
        {EXECUTION_STATUS_LABELS[execution.status] ?? execution.status}
      </span>
      {execution.underlyingRunId ? (
        <span className="text-muted-foreground">
          {execution.underlyingSystem} run {execution.underlyingRunId.slice(0, 8)}
        </span>
      ) : null}
      <span className="text-muted-foreground">
        {csFormatDateTime(finished ?? execution.startedAt ?? execution.createdAt)}
      </span>
      {execution.lastError ? (
        <span className="text-red-600 dark:text-red-400">{execution.lastError}</span>
      ) : null}
    </div>
  );
}

function DetailSection({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Separator />
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-1">
        {title}
      </div>
      {children}
    </div>
  );
}

function EmptyLine({ text }: { text: string }) {
  return <p className="text-xs text-muted-foreground">{text}</p>;
}
